import { formatPrice } from '../utils';

const formatApiDate = (date) => {
	if (!date) {
		return '';
	}

	const [year, month, day] = date.split('-');

	return year && month && day ? `${day}/${month}/${year}` : date;
};

const getVoucherId = ({
	orderId,
	serviceItemId,
	voucherReference,
	finalizedAt,
}) =>
	[orderId, serviceItemId, voucherReference, finalizedAt]
		.filter(Boolean)
		.join(':');

const mapVoucherToTableRow = (voucher, intl) => ({
	...voucher,
	id: getVoucherId(voucher),
	supplyChannelRecordLocator: voucher.marketingCarrierRef,
	bookingLocator: voucher.orderId,
	voucherNumber: voucher.voucherReference,
	issueDate: formatApiDate(voucher.voucherIssueDate),
	finalizedDate: formatApiDate(voucher.finalizedAt?.split('T')[0]),
	value: formatPrice(voucher.voucherAmount, voucher.currency, intl),
});

export const mapFinalizedVoucher = (voucher, intl) =>
	mapVoucherToTableRow(voucher, intl);

export const mapApiPagination = (pagination = {}, returnedVoucherCount = 0) => {
	const page = Number(pagination.page) || 1;
	const pageSize = Number(pagination.pageSize) || 0;
	const totalItems = Number(pagination.totalItems) || 0;
	const totalPages = Number(pagination.totalPages) || 0;
	const currentPage = Math.max(page - 1, 0);
	const resultStart = totalItems > 0 ? currentPage * pageSize + 1 : 0;
	const resultEnd =
		totalItems > 0
			? Math.min(resultStart + returnedVoucherCount - 1, totalItems)
			: 0;

	return {
		currentPage,
		pageSize,
		totalItems,
		totalPages,
		resultStart,
		resultEnd,
	};
};

const mapResponse = (response, vouchers) => ({
	vouchers,
	pagination: mapApiPagination(response?.pagination, vouchers.length),
});

export const mapFinalizedVouchersResponse = (response, intl) => {
	const voucherResponse = response || {};
	const vouchers = Array.isArray(voucherResponse.vouchers)
		? voucherResponse.vouchers.map((voucher) =>
				mapFinalizedVoucher(voucher, intl),
			)
		: [];

	return mapResponse(voucherResponse, vouchers);
};

export const mapVouchersToFinalizePayload = (vouchers) =>
	vouchers.map((voucher) => ({
		orderId: voucher.orderId,
		serviceItemId: voucher.serviceItemId,
		marketingCarrierRef: voucher.marketingCarrierRef,
		voucherAmount: voucher.voucherAmount,
		currency: voucher.currency,
		voucherIssueDate: voucher.voucherIssueDate,
		voucherReference: voucher.voucherReference,
	}));

const isSuccessfulFinalizeResult = ({ statusCode } = {}) =>
	statusCode >= 200 && statusCode < 300;

export const classifyFinalizeResponse = (response, submittedOrderCount) => {
	if (!Array.isArray(response) || response.length === 0) {
		return 'error';
	}

	const successfulResultCount = response.filter(
		isSuccessfulFinalizeResult,
	).length;
	const allSubmittedOrdersSucceeded =
		successfulResultCount === submittedOrderCount &&
		response.length === submittedOrderCount;

	if (allSubmittedOrdersSucceeded) {
		return 'success';
	}

	return successfulResultCount > 0 ? 'partial' : 'error';
};
